import "slick-carousel/slick/slick.css"
import "slick-carousel/slick/slick-theme.css"

import { Typography } from "antd"
import moment from "moment"
import PropTypes from "prop-types"
import React, { useEffect } from "react"
import Slider from "react-slick"
import styled from "styled-components"
import * as Yup from "yup"

import useFormikForm from "../../hooks/useFormikForm"
import StyledBottomSheet from "../BottomSheet"
import AppDatePicker from "../FormItems/AppDatePicker"
import FormInput from "../FormItems/FormInput"
import RoomCard from "../RoomCard"
import SearchBox from "../SearchBox"

// ----------------------------------------------------------------------------

function _RoomAllocation(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { className, rooms, search, allocateRoom, requestInfo, open } = props

    const { Title } = Typography

    const settings = {
        dots: true,
        infinite: false,
        speed: 300,
        slidesToShow: 4,
        arrows: true,
    }

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    const initialValues = {
        room: "",
        startDate: null,
        endDate: null,
        tenant: {
            fullName: "",
            fiscalCode: "",
            email: "",
            birthPlace: "",
            birthDate: null,
        },
    }

    const validationSchema = Yup.object().shape({
        room: Yup.string().required("Seleziona una stanza per procedere"),
        startDate: Yup.object().nullable().required("Campo obbligatorio"),
        endDate: Yup.mixed()
            .nullable()
            .required("Campo obbligatorio")
            .when("startDate", (startDate) => {
                return Yup.mixed()
                    .required("Campo obbligatorio")
                    .test(
                        "is-after-start",
                        "La data di fine deve essere dopo la data di inizio",
                        (value) => moment(value).isAfter(moment(startDate))
                    )
            }),
        tenant: Yup.object({
            fullName: Yup.string().required("Campo obbligatorio"),
            fiscalCode: Yup.string(),
            email: Yup.string()
                .email("L'indirizzo non è valido")
                .required("Campo obbligatorio"),
            birthPlace: Yup.string().required("Campo obbligatorio"),
            birthDate: Yup.object().nullable().required("Campo obbligatorio"),
        }).required(),
    })

    const {
        onChange,
        bulkChange,
        getFieldProps,
        errors,
        touched,
        handleSubmit,
        reset,
        values,
    } = useFormikForm({
        handleSubmit: allocateRoom,
        initialValues,
        validationSchema,
        initialTouched: { room: true },
    })

    // -------------------------------------
    // Effects
    // -------------------------------------

    useEffect(() => {
        !open && reset()
        // eslint-disable-next-line
    }, [open])

    useEffect(() => {
        if (requestInfo) {
            const { rawData: data } = requestInfo
            bulkChange({
                ...values,
                tenant: {
                    birthDate: moment(data.birthDate),
                    birthPlace: data?.birthPlace,
                    fullName: data?.fullName,
                    email: data.email,
                    fiscalCode: data.fiscalCode ?? "",
                },
            })
        }
    }, [requestInfo])

    // -------------------------------------
    // Component functions
    // -------------------------------------

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    return (
        <StyledBottomSheet
            className={`${className}`}
            open={props.open}
            onCancel={props.onCancel}
            snapPoint={80}
            title={props.title}
            onConfirm={handleSubmit}
        >
            <FormInput
                label="Nome e cognome"
                className="field"
                {...getFieldProps("tenant.fullName")}
            />

            <FormInput
                label="E-mail"
                className="field"
                {...getFieldProps("tenant.email")}
            />
            <FormInput
                label="Codice fiscale"
                className="field"
                {...getFieldProps("tenant.fiscalCode")}
            />
            <FormInput
                label="Luogo di nascita"
                className="field"
                {...getFieldProps("tenant.birthPlace")}
            />
            <AppDatePicker
                label="Data di nascita"
                className="field"
                {...getFieldProps("tenant.birthDate")}
            />
            <AppDatePicker
                label="Data di inizio contratto"
                className="field"
                {...getFieldProps("startDate")}
            />
            <AppDatePicker
                label="Data di fine contratto"
                className="field"
                {...getFieldProps("endDate")}
            />

            <div className="room-select">
                <Title level={5}>
                    {"Seleziona l'alloggio che vuoi assegnare a questo utente"}
                </Title>
                <SearchBox search={search} className="box" />
                <Slider {...settings}>
                    {rooms.map((room, i) => {
                        return (
                            <RoomCard
                                id={room.id}
                                imageSrc={room.images[0]}
                                options={room.options}
                                key={room.id}
                                allocateRoom={(room) => {
                                    onChange("room")(room)
                                }}
                                selected={values.room === room.id}
                            />
                        )
                    })}
                </Slider>
                {touched?.room && errors.room && (
                    <p className="room-error-msg">{errors.room}</p>
                )}
            </div>
        </StyledBottomSheet>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

_RoomAllocation.propTypes = {
    className: PropTypes.string.isRequired,
    allocateRoom: PropTypes.func,
    requestInfo: PropTypes.any,
    search: PropTypes.any,
    rooms: PropTypes.array,

    open: PropTypes.bool,
    title: PropTypes.string,
    onCancel: PropTypes.func,
}

_RoomAllocation.defaultProps = {}

// ----------------------------------------------------------------------------

const RoomAllocation = styled(_RoomAllocation)`
    & {
        padding-bottom: ${(props) => props.theme.spacing.bigPadding};
        .footer {
            margin-right: 40px;
            margin-bottom: 40px;
        }
        .field {
            width: 300px;
            margin-bottom: 20px;
        }
        h5 {
            margin: 45px 0 40px;
        }
        .room-select {
            margin-top: 100px;
        }
        .search-form {
            display: flex;
            align-items: center;
            justify-content: center;
        }
        .icon-button {
            width: 50px !important;
            height: 50px !important;
            overflow: hidden;
            border-radius: 50% !important;
            border: none !important;
            border-radius: 100%;
            margin: 0;
            padding: 0;
        }
        .slick-slider {
            .slick-track {
                padding: ${(props) => props.theme.spacing.padding} 0;
                margin-left: -${(props) => props.theme.spacing.padding};
                margin-right: -${(props) => props.theme.spacing.padding};
                .slick-slide {
                    padding: 0 ${(props) => props.theme.spacing.padding};
                }
            }
        }
        p.room-error-msg {
            color: ${({ theme }) => theme.colors.error};
        }
    }
`
// ----------------------------------------------------------------------------

export default RoomAllocation
